import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DEFAULT_BOARD_COLUMN_COLOR } from '../../lib/boardColumn.constants'
import {
  getBoardLaneItemKey,
  sortBoardItemsByPosition,
} from '../../lib/taskTrackerBoardItems.utils'
import type { TaskTrackerBoardColumn } from '../domain.types'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  createEmptyBoardEditBoardState,
  createInitialBoardEditState,
} from './boardEdit.state.utils'
import {
  createBoardColumnThunk,
  deleteBoardColumnThunk,
  deleteBoardSummaryThunk,
  deleteBoardThunk,
  fetchBoardEditDataThunk,
  reorderBoardLanesThunk,
  saveBoardColumnThunk,
  saveBoardTitleThunk,
} from './boardEdit.thunks'
import type { TaskTrackerBoardLaneItem } from '../domain.types'

const buildColumnDrafts = (columns: TaskTrackerBoardColumn[]) =>
  Object.fromEntries(
    columns.map((column) => [
      column.id,
      { title: column.title, color: column.color },
    ]),
  )

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardEditState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardEditBoardState()
  }
  return state.byBoardId[boardId]
}

const boardEditSlice = createSlice({
  name: 'taskTrackerBoardEdit',
  initialState: createInitialBoardEditState(),
  reducers: {
    resetBoardEditState(state, action: PayloadAction<number>) {
      state.byBoardId[action.payload] = createEmptyBoardEditBoardState()
    },
    setBoardEditBoardTitle(
      state,
      action: PayloadAction<{ boardId: number; title: string }>,
    ) {
      ensureBoard(state, action.payload.boardId).boardTitle =
        action.payload.title
    },
    setBoardEditColumnDraftField(
      state,
      action: PayloadAction<{
        boardId: number
        columnId: number
        field: 'title' | 'color'
        value: string
      }>,
    ) {
      const editState = ensureBoard(state, action.payload.boardId)
      const columnId = action.payload.columnId
      editState.columnDrafts[columnId] = {
        title: editState.columnDrafts[columnId]?.title ?? '',
        color:
          editState.columnDrafts[columnId]?.color ?? DEFAULT_BOARD_COLUMN_COLOR,
        [action.payload.field]: action.payload.value,
      }
    },
    setBoardEditNewColumnTitle(
      state,
      action: PayloadAction<{ boardId: number; title: string }>,
    ) {
      ensureBoard(state, action.payload.boardId).newColumnTitle =
        action.payload.title
    },
    setBoardEditNewColumnColor(
      state,
      action: PayloadAction<{ boardId: number; color: string }>,
    ) {
      ensureBoard(state, action.payload.boardId).newColumnColor =
        action.payload.color
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state.byBoardId[action.payload.boardId]
      })
      .addCase(fetchBoardEditDataThunk.pending, (state, action) => {
        const editState = ensureBoard(state, action.meta.arg)
        editState.loading = true
        editState.loadError = null
      })
      .addCase(fetchBoardEditDataThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.loading = false
        editState.board = action.payload.details.board
        editState.boardTitle = action.payload.details.board.title
        editState.savedBoardTitle = action.payload.details.board.title
        editState.boardItems = action.payload.details.boardItems
        editState.columns = action.payload.details.columns
        editState.columnDrafts = buildColumnDrafts(action.payload.details.columns)
      })
      .addCase(fetchBoardEditDataThunk.rejected, (state, action) => {
        const payload = action.payload as { boardId: number; error: string }
        const editState = ensureBoard(state, payload.boardId)
        editState.loading = false
        editState.loadError = payload.error
      })
      .addCase(saveBoardTitleThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).savingBoardTitle = true
      })
      .addCase(saveBoardTitleThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.savingBoardTitle = false
        editState.board = action.payload.board
        editState.boardTitle = action.payload.board.title
        editState.savedBoardTitle = action.payload.board.title
      })
      .addCase(saveBoardTitleThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).savingBoardTitle = false
      })
      .addCase(saveBoardColumnThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).savingColumnId =
          action.meta.arg.columnId
      })
      .addCase(saveBoardColumnThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.savingColumnId = null
        const columnId = action.payload.column.id
        editState.columns = [
          ...editState.columns.filter((item) => item.id !== columnId),
          action.payload.column,
        ].sort((a, b) => a.position - b.position)
        editState.boardItems = sortBoardItemsByPosition(
          editState.boardItems.map((item) =>
            item.type === 'column' && item.id === columnId
              ? { ...item, ...action.payload.column, type: 'column' as const }
              : item,
          ),
        )
        editState.columnDrafts[columnId] = {
          title: action.payload.column.title,
          color: action.payload.column.color,
        }
      })
      .addCase(saveBoardColumnThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).savingColumnId = null
      })
      .addCase(deleteBoardColumnThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingColumnId =
          action.meta.arg.columnId
      })
      .addCase(deleteBoardColumnThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.deletingColumnId = null
        editState.columns = editState.columns.filter(
          (item) => item.id !== action.payload.columnId,
        )
        editState.boardItems = editState.boardItems.filter(
          (item) =>
            !(item.type === 'column' && item.id === action.payload.columnId),
        )
        delete editState.columnDrafts[action.payload.columnId]
      })
      .addCase(deleteBoardColumnThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingColumnId = null
      })
      .addCase(deleteBoardSummaryThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingSummaryId =
          action.meta.arg.summaryId
      })
      .addCase(deleteBoardSummaryThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.deletingSummaryId = null
        editState.boardItems = editState.boardItems.filter(
          (item) =>
            !(item.type === 'summary' && item.id === action.payload.summaryId),
        )
      })
      .addCase(deleteBoardSummaryThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingSummaryId = null
      })
      .addCase(reorderBoardLanesThunk.pending, (state, action) => {
        const {
          boardId,
          activeType,
          activeId,
          overType,
          overId,
          orderedItems,
        } = action.meta.arg
        if (activeType === overType && activeId === overId) return

        const editState = ensureBoard(state, boardId)
        editState.reorderingLaneKey = `${activeType}:${activeId}`
        editState.reorderingPreviousBoardItems = editState.boardItems

        const itemsByKey = new Map(
          editState.boardItems.map((item) => [getBoardLaneItemKey(item), item]),
        )
        const reordered = orderedItems
          .map((item) => itemsByKey.get(`${item.type}:${item.id}`))
          .filter(Boolean)
          .map((item, index) => ({
            ...(item as TaskTrackerBoardLaneItem),
            position: index + 1,
          }))

        if (reordered.length === editState.boardItems.length) {
          editState.boardItems = sortBoardItemsByPosition(reordered)
        }
      })
      .addCase(reorderBoardLanesThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.meta.arg.boardId)
        editState.reorderingLaneKey = null
        editState.reorderingPreviousBoardItems = null
        if (!action.payload) return
        editState.boardItems = action.payload.updatedBoardItems
        editState.columns = action.payload.updatedColumns
      })
      .addCase(reorderBoardLanesThunk.rejected, (state, action) => {
        const editState = ensureBoard(state, action.meta.arg.boardId)
        editState.reorderingLaneKey = null
        if (editState.reorderingPreviousBoardItems) {
          editState.boardItems = editState.reorderingPreviousBoardItems
        }
        editState.reorderingPreviousBoardItems = null
      })
      .addCase(createBoardColumnThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).creatingColumn = true
      })
      .addCase(createBoardColumnThunk.fulfilled, (state, action) => {
        const editState = ensureBoard(state, action.payload.boardId)
        editState.creatingColumn = false
        const createdColumn = action.payload.column
        editState.columns = [...editState.columns, createdColumn].sort(
          (a, b) => a.position - b.position,
        )
        editState.boardItems = sortBoardItemsByPosition([
          ...editState.boardItems,
          {
            ...createdColumn,
            type: 'column' as const,
            cards: [],
            cardsCount: 0,
          },
        ])
        editState.columnDrafts[createdColumn.id] = {
          title: createdColumn.title,
          color: createdColumn.color,
        }
        editState.newColumnTitle = ''
        editState.newColumnColor = DEFAULT_BOARD_COLUMN_COLOR
      })
      .addCase(createBoardColumnThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).creatingColumn = false
      })
      .addCase(deleteBoardThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingBoard = true
      })
      .addCase(deleteBoardThunk.fulfilled, (state, action) => {
        ensureBoard(state, action.payload.boardId).deletingBoard = false
      })
      .addCase(deleteBoardThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingBoard = false
      })
  },
})

export const {
  resetBoardEditState,
  setBoardEditBoardTitle,
  setBoardEditColumnDraftField,
  setBoardEditNewColumnTitle,
  setBoardEditNewColumnColor,
} = boardEditSlice.actions

export default boardEditSlice.reducer
